import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { CampaignDatePickerComponent } from './campaign-date-picker/campaign-date-picker.component';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { MatTooltipModule } from '@angular/material/tooltip';
import { formatTimeFromDate } from './campaign-schedule-datetime.util';
/**
 * ScheduleComponent manages the scheduling section of a campaign editor, allowing users to select start/end date-times, set recurrence, and emit changes or completion events.
 * @component
 * @selector app-schedule
 * @example
 *   <app-schedule
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     [isAllSectionsCompleted]="false"
 *     (toggle)="handleToggle($event)"
 *     (done)="handleDone()"
 *     (dataChange)="handleDataChange($event)"
 *     (openPreview)="handleOpenPreview()"
 *   ></app-schedule>
 * @input boolean isExpanded - Controls whether the schedule section is expanded (collapsible state). Default: true.
 * @input any campaignData - Initial campaign schedule object (may include schedule_start_date, schedule_end_date, schedule_recurrence). Used to populate form fields on init / changes.
 * @input boolean isCompleted - Flag indicating if the schedule section is marked completed.
 * @input boolean isAllSectionsCompleted - Flag indicating whether all editor sections are completed; parent may use this to control preview behavior.
 * @output EventEmitter<boolean> toggle - Emitted when the schedule section is toggled; payload is the new expanded state.
 * @output EventEmitter<void> done - Emitted when the user marks the schedule as completed or finishes the section.
 * @output EventEmitter<any> dataChange - Emitted whenever the schedule form changes. Payload is an object with schedule_start_date and schedule_end_date as ISO strings or null, schedule_start_time and schedule_end_time as HH:MM:SS or null, and schedule_recurrence.
 * @output EventEmitter<void> openPreview - Emitted to request opening the preview modal after finishing (parent may check completion state before showing preview).
 * Purpose:
 *   - Provide UI and state management for campaign scheduling, including start/end date-time selection, recurrence choice, and basic validation.
 *   - Communicate form changes and completion intents to the parent component via events.
 * Key methods:
 *   - onScheduleToggle(expanded: boolean): Emits the toggle event with the expanded state.
 *   - onScheduleDone(): Emits the done event to mark the section as complete.
 *   - onFinish(): Validates required fields (ensures start date/time is set), shows validation messages via NotificationService, emits done and openPreview when appropriate.
 *   - onFormChange(): Builds the schedule payload (ISO strings or null), logs the change, and emits dataChange.
 *   - onStartDateChange(date: Date | null) / onEndDateChange(date: Date | null): Update corresponding date values and call onFormChange.
 *   - openStartDatePicker() / closeStartDatePicker() / openEndDatePicker() / closeEndDatePicker(): Control date picker modal visibility.
 *   - onStartDateSelected(date: Date): Set start date, clear end date if it is less than 30 minutes after the new start (with a warning), emit dataChange and close picker.
 *   - onEndDateSelected(date: Date): Validate end date is at least 30 minutes after start (or not in the past if start is unset), set end date, emit dataChange and close picker.
 *   - getCurrentDateTime(): Date - Returns the current Date used to prevent selecting past end dates.
 *   - getEndDateMinDate(): Date - Returns the minimum selectable end date (startDateTime + 30 minutes if set, otherwise current time).
 * Lifecycle hooks:
 *   - ngOnInit(): Initializes form fields from campaignData when the component mounts.
 *   - ngOnChanges(): Updates form fields whenever campaignData input changes.
 * Notes:
 *   - Uses an injected NotificationService to display validation errors and warnings to the user.
 *   - syncFromCampaignData() rebuilds startDateTime/endDateTime from the *_date instants only.
 *     schedule_start_time is a server-local rendering kept for the scheduler, not the user's
 *     wall clock, so merging it in misread the time by the browser's UTC offset.
 *   - startDateTime is required to finish; endDateTime is required and validated only for recurring schedules.
 */
@Component({
  selector: 'app-schedule',
  imports: [
    CollapsibleSectionComponent,
    FormsModule,
    CampaignDatePickerComponent,
    CommonModule,
    MatTooltipModule,
  ],
  templateUrl: './schedule.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './schedule.component.scss',
})
export class ScheduleComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Input() isAllSectionsCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();
  @Output() openPreview = new EventEmitter<void>();

  // Form properties
  startDateTime: Date | null = null;
  endDateTime: Date | null = null;
  selectedFrequency = 'no_recurrence';

  private static readonly RECURRING_FREQUENCIES = new Set(['daily', 'weekly', 'monthly']);
  private static readonly MIN_END_OFFSET_MS = 30 * 60 * 1000;
  showStartDatePicker = false;
  showEndDatePicker = false;

  constructor(private messageService: NotificationService) {}

  ngOnInit(): void {
    this.syncFromCampaignData();
  }

  ngOnChanges(): void {
    this.syncFromCampaignData();
  }

  private syncFromCampaignData(): void {
    if (this.campaignData) {
      // Rebuild from the *_date instants alone.
      //
      // `schedule_start_time` is a SERVER-LOCAL rendering of schedule_start_date, derived in
      // campaignService.editCampaign for the scheduler's benefit (it recombines the two with
      // server-local Y/M/D). It is NOT the user's wall clock. Merging it in here made a UTC
      // server redisplay a 10:00 AM IST pick as 4:30 AM -- shifted by the browser's offset.
      // It looked correct only where the server and browser shared a zone, which is why the
      // bug shows on dev/QA (UTC) but not on a local stack running in IST.
      //
      // The instant already carries the time and renders correctly in any browser zone.
      this.startDateTime = this.campaignData.schedule_start_date
        ? new Date(this.campaignData.schedule_start_date)
        : null;
      this.endDateTime = this.campaignData.schedule_end_date
        ? new Date(this.campaignData.schedule_end_date)
        : null;
      this.selectedFrequency = this.campaignData.schedule_recurrence || 'no_recurrence';
    }
  }

  onScheduleToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onScheduleDone(): void {
    if (!this.canFinish()) {
      this.showFinishValidationError();
      return;
    }
    this.done.emit();
  }

  /** True when Daily, Weekly, or Monthly is selected — end date is required. */
  isRecurringFrequencySelected(): boolean {
    return ScheduleComponent.RECURRING_FREQUENCIES.has(this.selectedFrequency);
  }

  /** Whether the Finish action is allowed from the current form state. */
  canFinish(): boolean {
    if (!this.startDateTime) {
      return false;
    }
    if (this.isStartDateInPast()) {
      return false;
    }
    if (this.isRecurringFrequencySelected()) {
      if (!this.endDateTime || !this.isEndDateTimeValid(this.endDateTime)) {
        return false;
      }
    }
    return true;
  }

  isStartDateInPast(): boolean {
    return !!this.startDateTime && this.startDateTime < this.getCurrentDateTime();
  }

  get finishButtonTooltip(): string {
    if (this.isStartDateInPast()) {
      return 'Start date & time cannot be in the past';
    }
    if (!this.isAllSectionsCompleted) {
      return 'Complete all sections for finishing campaign';
    }
    if (!this.canFinish()) {
      return 'Please complete the schedule before finishing';
    }
    return '';
  }

  private showFinishValidationError(): void {
    if (!this.startDateTime) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a start date & time before finishing.',
        life: 3000,
      });
      return;
    }
    if (this.isStartDateInPast()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Start date & time cannot be in the past.',
        life: 3000,
      });
      return;
    }
    if (this.isRecurringFrequencySelected() && !this.endDateTime) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select an end date & time for recurring schedules.',
        life: 3000,
      });
      return;
    }
    if (
      this.isRecurringFrequencySelected() &&
      this.endDateTime &&
      !this.isEndDateTimeValid(this.endDateTime)
    ) {
      this.showEndDateValidationError();
    }
  }

  /**
   * Validate that a start date/time is present and, if valid, emit completion and preview events to finalize the schedule.
   * @example
   * nFinish()
   * undefined
   * @returns {{void}} Emits 'done' and 'openPreview' events to mark the schedule complete and trigger the preview; returns nothing.
   **/
  onFinish(): void {
    if (!this.canFinish()) {
      this.showFinishValidationError();
      return;
    }

    this.done.emit();
    this.openPreview.emit();
  }

  onFormChange(): void {
    if (!this.isRecurringFrequencySelected()) {
      this.endDateTime = null;
    }
    const data = {
      schedule_start_date: this.startDateTime ? this.startDateTime.toISOString() : null,
      schedule_start_time: formatTimeFromDate(this.startDateTime),
      schedule_end_date: this.endDateTime ? this.endDateTime.toISOString() : null,
      schedule_end_time: formatTimeFromDate(this.endDateTime),
      schedule_recurrence: this.selectedFrequency,
    };
    console.log('Schedule form change - emitting data:', data);
    this.dataChange.emit(data);
  }

  onStartDateChange(date: Date | null): void {
    this.startDateTime = date;
    this.onFormChange();
  }

  onEndDateChange(date: Date | null): void {
    this.endDateTime = date;
    this.onFormChange();
  }

  // Date picker modal methods
  openStartDatePicker(): void {
    this.showStartDatePicker = true;
  }

  closeStartDatePicker(): void {
    this.showStartDatePicker = false;
  }

  openEndDatePicker(): void {
    this.showEndDatePicker = true;
  }

  closeEndDatePicker(): void {
    this.showEndDatePicker = false;
  }

  /**
   * Handle selection of a new start date/time: update startDateTime, clear endDateTime if it is less than 30 minutes after the new start (with a warning), trigger form change handling, and close the start date picker.
   * @example
   * nStartDateSelected(new Date('2026-03-17T10:00:00Z'))
   * undefined
   * @param {Date} date - The newly selected start date and time.
   * @returns {void} Returns nothing.
   **/
  onStartDateSelected(date: Date): void {
    this.startDateTime = date;

    // If end date exists and is now less than 30 minutes after start, clear it
    if (this.endDateTime && !this.isEndDateTimeValid(this.endDateTime)) {
      this.endDateTime = null;
      this.messageService.add({
        severity: 'warn',
        summary: 'End Date Cleared',
        detail:
          'End date & time has been cleared because it was less than 30 minutes after the new start date & time.',
        life: 3000,
      });
    }

    this.onFormChange();
    this.closeStartDatePicker();
  }

  /**
   * Validate and set the selected end date/time, showing errors if invalid.
   * @example
   * nEndDateSelected(new Date('2026-03-17T12:00:00Z'))
   * undefined
   * @param {{Date}} {{date}} - Selected end date and time to validate and set.
   * @returns {{void}} No return value.
   **/
  onEndDateSelected(date: Date): void {
    if (!this.isEndDateTimeValid(date)) {
      this.showEndDateValidationError();
      return;
    }

    this.endDateTime = date;
    this.onFormChange();
    this.closeEndDatePicker();
  }

  getCurrentDateTime(): Date {
    return new Date();
  }

  /**
   * Earliest selectable end date/time: start + 30 minutes when a start is set, otherwise now.
   * Bound to the end date picker's minDate so dates before start and times before start+30min are disabled.
   */
  getEndDateMinDate(): Date {
    if (this.startDateTime) {
      return new Date(this.startDateTime.getTime() + ScheduleComponent.MIN_END_OFFSET_MS);
    }
    return this.getCurrentDateTime();
  }

  /** True when end is at or after the picker's minimum (start + 30 minutes, or now if start is unset). */
  private isEndDateTimeValid(end: Date): boolean {
    return end >= this.getEndDateMinDate();
  }

  private showEndDateValidationError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: this.startDateTime
        ? 'End Date & Time must be at least 30 minutes after Start Date & Time.'
        : 'End date & time cannot be in the past.',
      life: 3000,
    });
  }
}
